import { inject, Injectable } from '@angular/core';
import { TeamMember } from '@interfaces/code-team/team-member';
import { RoleData } from '@interfaces/role/role-data';
import { RoleDataRequest } from '@interfaces/role/role-data-request';
import { RoleFormRequest } from '@interfaces/role/role-form-request';
import { RoleUpdateRequest } from '@interfaces/role/role-update-request';
import { UserUpdateRequest } from '@interfaces/user/user-update-request';
import { RoleStore } from '@stores/role.store';
import { TopicType } from '../enums/topic-type';
import { AuthService } from './auth.service';
import { WebSocketService } from './web-socket.service';
import { NewTeamMember } from '@interfaces/code-team/new-team-member';

@Injectable({
  providedIn: 'root',
})
export class RoleService {
  private webSocketService = inject(WebSocketService);
  private authService = inject(AuthService);
  private roleStore = inject(RoleStore);
  roles = this.roleStore.roles;
  setRoles(roles: RoleData[]) {
    this.roleStore.setRoles(roles);
  }

  sendRoleDataRequest() {
    const userId = this.authService.user().id;
    const message: RoleDataRequest = {
      requester: userId!,
    };

    this.webSocketService.send({ topic: TopicType.ROLE_DATA_REQUEST, message });
  }

  updatePermissions(roleFormRequest: RoleFormRequest) {
    roleFormRequest.roles.forEach((role) => {
      const userId = this.authService.user().id;
      const permissions = role.permissions.reduce((res, permission) => {
        return {
          ...res,
          [permission.permissionName]: {
            isEnabled: !!permission.isEnabled,
            iconPosition: permission.iconPosition,
          },
        };
      }, {});
      const message: RoleUpdateRequest = {
        requester: userId!,
        ekoUserRole: role.ekoUserRole,
        permissions: permissions,
      };

      this.webSocketService.send({
        topic: TopicType.PERMISSION_UPDATE_REQUEST,
        message,
      });
    });
  }

  createNewTeamMembers(newTeamMembers: NewTeamMember) {
    const userId = this.authService.user().id;
    let uuid = this.authService.uuid;
    const message = {
      key: uuid,
      requester: userId!.replace('@', ''),
      userType: newTeamMembers.userType,
      userData: {
        username: newTeamMembers.userId,
        departments: newTeamMembers.departments,
        role: newTeamMembers.role,
        password: newTeamMembers.password,
        firstName: newTeamMembers.firstName,
        lastName: newTeamMembers.lastName,
      },
    };
    this.webSocketService.send({
      topic: TopicType.USER_CREATE_REQUEST,
      message,
    });

    return uuid;
  }

  removeTeamMember(userName: string) {
    const userId = this.authService.user().id;
    let uuid = this.authService.uuid;
    const message = {
      key: uuid,
      requester: userId!.replace('@', ''),
      username: userName,
    };
    this.webSocketService.send({
      topic: TopicType.USER_DELETE_REQUEST,
      message,
    });
  }

  /**
   * Update the roles of the users in the team
   * @param teamMembers - The team members to update
   * todo: move to the user service
   */
  updateTeamMembers(teamMembers: TeamMember[]) {
    const userId = this.authService.user().id;
    for (const tm of teamMembers) {
      const message: UserUpdateRequest = {
        key: tm.id,
        requester: userId!,
        userId: tm.id,
        data: {
          role: tm.role,
          primaryDepartment: tm.primaryDepartment,
          departments: tm.departments,
          hasAccess: tm.hasAccess,
          userType: tm.userType,
          ...(tm.password !== '***************' && { password: tm.password }),

          ...(tm.notificationSettings &&
          Array.isArray(tm.notificationSettings.popupNotifications) &&
          tm.notificationSettings.popupNotifications.length > 0
            ? { notificationSettings: tm.notificationSettings }
            : {}),
        },
      };

      this.webSocketService.send({
        topic: TopicType.USER_UPDATE_REQUEST,
        message,
      });
    }
  }
}
